import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plane, Hotel, Car } from 'lucide-react';

// Cheapora's top-level travel category nav — shared by Home, Hotels and
// Cars. The three buttons are Cheapora's OWN navigation and never insert
// into or modify the Travelpayouts WL component.
//
// Behaviour (as of latest product spec):
//   • Flights → homepage Travelpayouts search section (`/#flights-search`).
//     - Same page: preventDefault + smooth scrollIntoView.
//     - Other page: full document navigation so the TP WL bundle boots
//       cleanly on the fresh Home render, then Home scrolls to the anchor.
//   • Hotels → SPA nav to /hotels
//   • Cars   → SPA nav to /cars
// The "active" pill is derived from the current pathname so users always
// see which category they're on.

export const FLIGHTS_ANCHOR_ID = 'flights-search';

export default function CategoryNav({ align = 'left' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === '/';
  const onHotels = location.pathname.startsWith('/hotels');
  const onCars = location.pathname.startsWith('/cars');

  const handleFlights = (e) => {
    if (onHome) {
      e.preventDefault();
      const el = document.getElementById(FLIGHTS_ANCHOR_ID);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Otherwise: let the anchor perform a full document navigation so the
    // Travelpayouts WL bundle re-executes cleanly on the fresh Home render.
  };

  const items = [
    {
      key: 'flights',
      label: 'Flights',
      icon: Plane,
      active: onHome,
      href: `/#${FLIGHTS_ANCHOR_ID}`,
      onClick: handleFlights,
    },
    {
      key: 'hotels',
      label: 'Hotels',
      icon: Hotel,
      active: onHotels,
      href: '/hotels',
      onClick: (e) => { e.preventDefault(); navigate('/hotels'); },
    },
    {
      key: 'cars',
      label: 'Cars',
      icon: Car,
      active: onCars,
      href: '/cars',
      onClick: (e) => { e.preventDefault(); navigate('/cars'); },
    },
  ];

  return (
    <div
      className={`flex flex-wrap gap-2 mb-3 md:mb-4 ${align === 'center' ? 'justify-center' : ''}`}
      role="tablist"
      aria-label="Cheapora travel category"
      data-testid="cheapora-category-nav"
    >
      {items.map(({ key, label, icon: Icon, active, href, onClick }) => (
        <a
          key={key}
          href={href}
          onClick={onClick}
          role="tab"
          aria-selected={active}
          data-testid={`category-nav-${key}`}
          className={
            'inline-flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-sm md:text-[15px] font-semibold border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 no-underline ' +
            (active
              ? 'bg-teal-700 text-white border-teal-700 shadow-md cursor-default'
              : 'bg-white text-slate-800 border-slate-200 hover:bg-teal-50 hover:text-teal-800 hover:border-teal-200 hover:-translate-y-0.5 active:translate-y-0')
          }
        >
          <Icon className={'w-4 h-4 ' + (active ? 'text-amber-300' : 'text-teal-700')} />
          <span>{label}</span>
        </a>
      ))}
    </div>
  );
}
